import { useRef } from 'react';
import type { ChangeEventHandler } from 'react';
import { inputPlaceholderYear } from 'lib/display-year';
import { useTranslations } from 'next-intl';
import styles from './date-controls.module.scss';
import ScreenReaderOnly from 'components/accesibility/screen-reader-only';
import { DebounceInput } from 'react-debounce-input';

export const DateControls = ({
	localSearchParams,
	handleYearFromChange,
	handleYearToChange,
	yearsAreValid,
	yearsMin,
	yearsMax,
}: Props) => {
	const t = useTranslations('collection.search.date');

	// all of this yearsRangeError code is solely for the a11y DOM
	// we set the error message but don't modify anything else on the FE
	// on the backend, we ignore the year range until it's valid
	const yearFromRef = useRef(null);
	const yearsRangeError = yearsAreValid ? false : t('yearsRangeError');

	return (
		<fieldset className={styles.dateFieldContainer}>
			{/* legend is proper accessibility semantics but it is visually forced to the top of a fieldset */}
			<ScreenReaderOnly text={t('negativeNumberTooltip')} Element="legend" />
			<div className={styles.controlsWrapper}>
				<div className={styles.inputWrapper}>
					<label
						htmlFor="yearFrom"
						className={[styles.fromLabel, styles.dateLabel].join(' ')}
					>
						{t('from')}
					</label>
					<DebounceInput
						debounceTimeout={400}
						className={[
							styles.fromInput,
							styles.dateLabel,
							styles.yearInput,
						].join(' ')}
						type="text"
						tabIndex={0}
						aria-invalid={!!yearsRangeError}
						aria-errormessage={!!yearsRangeError && yearsRangeError}
						id="yearFrom"
						ref={yearFromRef}
						onChange={handleYearFromChange}
						placeholder={inputPlaceholderYear(yearsMin)}
						value={localSearchParams.get('yearFrom') || ''} // yes the "" is needed to keep it a controlled component
					/>
				</div>

				<div className={styles.dashContainer}>&ndash;</div>

				<div className={styles.inputWrapper}>
					<label
						className={[styles.toLabel, styles.dateLabel].join(' ')}
						htmlFor="yearTo"
					>
						{t('to')}
					</label>
					<DebounceInput
						debounceTimeout={400}
						className={[
							styles.toInput,
							styles.dateLabel,
							styles.yearInput,
						].join(' ')}
						type="text"
						tabIndex={0}
						aria-invalid={!!yearsRangeError}
						id="yearTo"
						onChange={handleYearToChange}
						placeholder={inputPlaceholderYear(yearsMax)}
						value={localSearchParams.get('yearTo') || ''} // yes "" is needed to keep it a controlled component
					/>
				</div>
			</div>
			{/* aria-hide this because it's redundant to a screenreader, we already used legend above */}
			<div className={styles.legend} aria-hidden="true">
				{t('negativeNumberTooltip')}
			</div>
		</fieldset>
	);
};

export interface Props {
	localSearchParams: URLSearchParams;
	handleYearFromChange: ChangeEventHandler<HTMLInputElement>;
	handleYearToChange: ChangeEventHandler<HTMLInputElement>;
	yearsAreValid: boolean;
	yearsMin: number;
	yearsMax: number;
}
